import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getMatches } from "../../api/communityApi";

const MatchCard = ({ match }) => (
  <Link
    to={`/community/profile/${match.user_id}`}
    className="flex-shrink-0 w-[80vw] sm:w-64 rounded-2xl bg-white dark:bg-[#0b1220] border border-gray-200 dark:border-white/10 p-4 hover:-translate-y-1 transition-all"
  >
    <div className="flex items-center gap-3 mb-3">
      <img
        src={match.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(match.display_name)}`}
        alt=""
        className="w-12 h-12 rounded-full"
      />
      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate">{match.display_name}</p>
        <p className="text-xs text-gray-500 truncate">
          {match.is_agent ? `${match.agent_tagline}` : `@${match.username}`}
        </p>
      </div>
      {match.is_agent && (
        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-500">
          BOT
        </span>
      )}
    </div>

    <div className="space-y-2 text-xs">
      <div className="flex justify-between">
        <span className="text-gray-500">Style overlap</span>
        <span className="font-semibold">
          {Math.round(match.overlap_score * 100)}%
        </span>
      </div>
      <div className="w-full h-1.5 rounded-full bg-gray-200 dark:bg-white/10 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-emerald-400 to-purple-500"
          style={{ width: `${Math.round(match.overlap_score * 100)}%` }}
        />
      </div>

      {match.common_count > 0 && (
        <div className="flex justify-between">
          <span className="text-gray-500">Shared holdings</span>
          <span className="font-semibold">
            {match.common_count} in common
          </span>
        </div>
      )}

      {match.top_sector && (
        <div className="flex justify-between">
          <span className="text-gray-500">Their bias</span>
          <span className="font-semibold">{match.top_sector}</span>
        </div>
      )}
    </div>
  </Link>
);

const MatchmakingRow = () => {
  const [matchType, setMatchType] = useState("similar");
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emptyMsg, setEmptyMsg] = useState(null);
  const [optedOut, setOptedOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await getMatches(matchType);
        if (cancelled) return;
        setOptedOut(Boolean(data?.opted_out));
        setMatches(Array.isArray(data?.matches) ? data.matches : []);
        setEmptyMsg(data?.detail || null);
      } catch {
        if (!cancelled) setMatches([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [matchType]);

  // User turned matchmaking off in Settings — hide the row entirely.
  if (optedOut) return null;

  return (
    <div className="mb-10">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-purple-500 mb-1">
            AI Matchmaking
          </p>
          <h2 className="text-2xl font-bold">
            Investors {matchType === "similar" ? "like you" : "who challenge your view"}
          </h2>
        </div>
        <div className="flex gap-2 bg-gray-100 dark:bg-white/5 rounded-lg p-1">
          <button
            onClick={() => setMatchType("similar")}
            className={`px-3 py-1.5 rounded-md text-sm font-semibold transition ${
              matchType === "similar"
                ? "bg-white dark:bg-white/10 shadow-sm"
                : "text-gray-500 hover:text-gray-700 dark:hover:text-white"
            }`}
          >
            Similar
          </button>
          <button
            onClick={() => setMatchType("opposite")}
            className={`px-3 py-1.5 rounded-md text-sm font-semibold transition ${
              matchType === "opposite"
                ? "bg-white dark:bg-white/10 shadow-sm"
                : "text-gray-500 hover:text-gray-700 dark:hover:text-white"
            }`}
          >
            Opposite
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm">Finding matches…</div>
      ) : matches.length === 0 ? (
        <div className="text-gray-500 text-sm">
          {emptyMsg || "No matches yet — sync your holdings to enable matchmaking."}
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {matches.map((m) => (
            <MatchCard key={m.user_id} match={m} />
          ))}
        </div>
      )}
    </div>
  );
};

export default MatchmakingRow;
